import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Textarea, Select } from "@/components/ui/Primitives";
import { Wrench, Plus, ArrowRight, CheckCircle2, AlertCircle, CalendarClock, Archive } from "lucide-react";
import { toast } from "sonner";

// Column definitions for the maintenance pipeline. `match` picks which
// tickets belong in the column based on the raw status string.
const PIPELINE = [
  { key: "open", title: "Open", tone: "amber", icon: AlertCircle,
    match: (s) => !s || s === "open" || s === "new" || s === "submitted" },
  { key: "in_progress", title: "In progress", tone: "blue", icon: CalendarClock,
    match: (s) => s === "in_progress" || s === "assigned" || s === "scheduled" },
  { key: "resolved", title: "Resolved", tone: "emerald", icon: CheckCircle2,
    match: (s) => s === "resolved" || s === "closed" || s === "completed" },
  { key: "archived", title: "Archived", tone: "slate", icon: Archive,
    match: (s) => s === "archived" || s === "cancelled" || s === "withdrawn" },
];

export default function Tickets() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ unit_id: "", title: "", description: "", priority: "medium" });

  const isTenant = user?.role === "tenant";

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get("/tickets")];
      if (!isTenant) calls.push(api.get("/units"));
      const res = await Promise.all(calls);
      setItems(res[0].data || []);
      if (!isTenant) setUnits(res[1].data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Group tickets into pipeline columns.
  const columns = useMemo(() => {
    const byKey = Object.fromEntries(PIPELINE.map((c) => [c.key, []]));
    for (const t of items) {
      const col = PIPELINE.find((c) => c.match(t.status)) || PIPELINE[0];
      byKey[col.key].push(t);
    }
    return byKey;
  }, [items]);

  // Landlord-only: move a ticket to a new pipeline stage. Fire-and-forget
  // update — refresh on success. Failures fall through with a toast.
  const setStatus = async (ticketId, newStatus) => {
    try {
      await api.patch(`/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Moved to ${newStatus.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update status");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tickets", {
        unit_id: form.unit_id || null,
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
      });
      toast.success("Ticket submitted");
      setOpen(false);
      setForm({ unit_id: "", title: "", description: "", priority: "medium" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create ticket");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div data-testid="tickets-page">
      <PageHeader
        title="Maintenance"
        subtitle={isTenant ? "Submit and track repair requests for your home." : "Kanban pipeline — drag tickets across stages as they progress."}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-ticket-button"><Plus className="w-4 h-4"/> New ticket</Button>}
      />

      {items.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title="No tickets yet"
          description={isTenant ? "Need a repair? Submit a request and your landlord will respond." : "Tenants haven't submitted any maintenance requests yet."}
          action={null}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="tickets-pipeline">
          {PIPELINE.map((col) => {
            const Icon = col.icon;
            const rows = columns[col.key];
            return (
              <div key={col.key} data-testid={`pipeline-column-${col.key}`}
                className={`rounded-2xl bg-${col.tone}-50 border border-${col.tone}-200 p-3 min-h-[8rem]`}>
                <div className="flex items-center justify-between px-1 mb-2">
                  <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-extrabold text-${col.tone}-800`}>
                    <Icon className="w-3.5 h-3.5"/> {col.title}
                  </div>
                  <div className={`text-xs font-bold text-${col.tone}-700 tabular-nums`}>{rows.length}</div>
                </div>
                <div className="space-y-2">
                  {rows.length === 0 && (
                    <div className="text-[11px] text-slate-500 italic px-1 py-3 text-center">Nothing here.</div>
                  )}
                  {rows.map((t) => (
                    <div key={t.ticket_id} data-testid={`ticket-card-${t.ticket_id}`}
                      className="bg-white border border-slate-200 rounded-xl p-3 card-lift">
                      <Link to={`/tickets/${t.ticket_id}`} className="block">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-slate-900 text-sm leading-tight truncate flex-1">{t.title}</h3>
                          <PriorityPill p={t.priority}/>
                        </div>
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2">{t.description}</p>
                        <div className="mt-2 text-[10px] text-slate-500">{formatDate(t.created_at)}</div>
                      </Link>
                      {!isTenant && (
                        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1 flex-wrap" data-testid={`ticket-actions-${t.ticket_id}`}>
                          {PIPELINE.filter((p) => p.key !== col.key).map((p) => (
                            <button key={p.key} type="button" onClick={() => setStatus(t.ticket_id, p.key)}
                              data-testid={`ticket-move-${t.ticket_id}-to-${p.key}`}
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-${p.tone}-100 text-${p.tone}-800 hover:bg-${p.tone}-200 border border-${p.tone}-200`}>
                              → {p.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New maintenance ticket" testid="new-ticket-modal">
        <form onSubmit={submit} className="space-y-4">
          {!isTenant && units.length > 0 && (
            <Select label="Unit (optional)" value={form.unit_id} onChange={(e)=>setForm({...form, unit_id:e.target.value})} testid="ticket-unit-select">
              <option value="">— None / Property-level —</option>
              {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.label}</option>)}
            </Select>
          )}
          <Input label="Title" required placeholder="Leaky kitchen faucet" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} testid="ticket-title-input"/>
          <Textarea label="Describe the issue" required placeholder="Provide as much detail as possible…" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} testid="ticket-description-input"/>
          <Select label="Priority" value={form.priority} onChange={(e)=>setForm({...form, priority:e.target.value})} testid="ticket-priority-select">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </Select>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="ticket-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="ticket-create-submit">{saving ? "Submitting…" : "Submit ticket"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export function StatusPill({ s }) {
  if (s === "resolved") return <Pill tone="green"><CheckCircle2 className="w-3 h-3"/> Resolved</Pill>;
  if (s === "in_progress") return <Pill tone="blue"><CalendarClock className="w-3 h-3"/> In progress</Pill>;
  return <Pill tone="amber"><AlertCircle className="w-3 h-3"/> Open</Pill>;
}
export function PriorityPill({ p }) {
  const map = { low: "slate", medium: "amber", high: "red" };
  return <Pill tone={map[p] || "slate"}>{p?.toUpperCase()}</Pill>;
}
